import { type CardProps, Table } from 'antd';
import styled, { type DefaultTheme, css } from 'styled-components';

import { Alert, Card } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';
import { FlexContainer, SpacedContainer } from 'utils/layouts/containers';

type TableCardProps = CardProps & {
  clickable?: boolean;
  fillWidth?: boolean;
  disableUserSort?: boolean;
  theme?: DefaultTheme;
};

export const HeaderContainer = styled(FlexContainer)`
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

export const Control = styled(SpacedContainer)`
  margin-left: 0;
`;

export const TitleContainer = styled(Control)`
  margin-right: 1rem;
`;

export const TableCard = styled(
  ({
    clickable: _clickable,
    fillWidth: _fillWidth,
    disableUserSort: _disableUserSort,
    theme: _theme,
    ...rest
  }: TableCardProps) => <Card {...rest} />
)`
  .ant-table-thead {
    white-space: nowrap;
  }
  .ant-card-head {
    border-width: 1px;
    padding: 0 1em !important;
  }
  .ant-card-head-wrapper {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .ant-card-head-title {
    max-width: 100%;
    padding: 16px 0;
    padding-right: 1em; /* prevents overflow state while loading */
  }
  .ant-card-extra {
    padding: 1rem 0;
  }

  .ant-spin-blur {
    opacity: ${({ theme }) => (theme.themeName === Theme.Dark ? 0.2 : 0.5)};
  }

  .hidden-column {
    display: none !important;
  }

  tr {
    cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  }

  width: ${props => (props.fillWidth ? '100%' : 'auto')};

  ${({ disableUserSort }) =>
    disableUserSort
      ? `.ant-table-column-has-sorters {
    cursor: default !important;
    pointer-events: none;
  }`
      : ''};
`;

export const DeferBanner = styled(Alert)`
  border-radius: 0;
  border-width: 0 0 1px 0;
  animation: slide-down 0.4s ease-out;

  @keyframes slide-down {
    0% {
      opacity: 0;
      margin-top: -40px;
    }

    100% {
      opacity: 1;
      margin-top: 0;
    }
  }
`;

export const DraggableRow = styled.tr<{
  $isDraggable: boolean;
  $transform?: string;
}>`
  &:not(.ant-table-expanded-row) {
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'default')} !important;
    transform: ${({ $transform }) => $transform ?? 'unset'};
  }
`;

export const ArchivedRowStyle = css`
  .ant-table-row.archived > td,
  .ant-table-row.archived:hover > td,
  .ant-table-row.archived > td.ant-table-cell-row-hover {
    color: ${({ theme }) => theme.labelColor};
    background-color: ${({ theme }) =>
      theme.themeName === Theme.Dark ? theme.disabledBackground : theme.selectedListItemBackground};
    font-style: italic;
    border-bottom: 1px solid ${({ theme }) => theme.borderAccentColor};
    border-top: 1px solid ${({ theme }) => theme.borderAccentColor};
  }
`;

export const MarkedTableRowStyle = css`
  .ant-table-row.marked-table-row > td,
  .ant-table-row.marked-table-row:hover > td,
  .ant-table-row.marked-table-row > td.ant-table-cell-row-hover {
    color: ${({ theme }) => theme.labelColor};
    background-color: ${({ theme }) => theme.alternatingTableRowColor};
    border-bottom: 1px solid ${({ theme }) => theme.borderAccentColor};
    border-top: 1px solid ${({ theme }) => theme.borderAccentColor};
  }
`;

// When a row is selected, transition to selected color in 0.5s;
// when deselected, fade selected color out in 1.5s
export const SelectedRowTransitionStyle = css`
  tr.ant-table-row.ant-table-row-selected > td {
    -webkit-transition: background-color 0.5s ease-in;
    -moz-transition: background-color 0.5s ease-in;
    -o-transition: background-color 0.5s ease-in;
    transition: background-color 0.5s ease-in;
  }
  tr.ant-table-row:not(.ant-table-row-selected) > td {
    -webkit-transition: background-color 1.5s ease-in;
    -moz-transition: background-color 1.5s ease-in;
    -o-transition: background-color 1.5s ease-in;
    transition: background-color 1.5s ease-in;
  }
`;

/**
 * Flashes the row that was just moved with the priority Up/Down controls, so its new
 * position is noticeable without watching a drag. Must come after
 * `SelectedRowTransitionStyle` to win the transition timing; once the class is dropped,
 * that slower transition fades the highlight back out.
 */
export const MovedTableRowStyle = css`
  tr.ant-table-row.priority-moved-row > td,
  tr.ant-table-row.priority-moved-row:hover > td {
    background-color: ${({ theme }) => theme.tealLightOpacity} !important;
    transition: background-color 0.15s ease-in;
  }
`;

// `styled()` erases Table's generic to `unknown`, which rejects typed columns, and a generic cast
// here is erased again by the files that wrap StyledTable in their own `styled()`.
export const StyledTable = styled(Table<any>)<{ $draggingTable?: boolean }>`
  ${ArchivedRowStyle}
  ${MarkedTableRowStyle}
  ${SelectedRowTransitionStyle}
  ${MovedTableRowStyle}

  .ant-table {
    ${({ theme }) =>
      theme.themeName === Theme.Dark
        ? css`
            scrollbar-color: unset;
          `
        : ''}

    thead > tr > .ant-table-selection-column,
    thead > tr > .ant-table-cell-scrollbar {
      box-shadow: none;
    }

    th.ant-table-cell {
      height: 58px;
      padding: 0 16px;

      /* antd 5 sets the header icons 4px after the title; give the title room. */
      .ant-table-column-sorter,
      .ant-table-column-title:not(:has(.ant-table-column-sorters)) + .ant-table-filter-trigger {
        margin-inline-start: 24px;
      }

      .ant-dropdown-trigger {
        border-radius: unset;
        height: 56px;
      }
    }
  }

  .ant-table-row-expand-icon {
    border-radius: 2px !important;
  }

  /*
   * antd draws pager tiles and the prev and next buttons borderless; the DataTable pager boxes
   * them. Prev and next only: the jump-prev and jump-next links are inline around a block, and
   * a border there gives them a line box that drops their ellipsis below the pager.
   */
  .ant-pagination-item {
    border-radius: 2px !important;

    &:not(.ant-pagination-item-active) {
      border: 1px solid ${({ theme }) => theme.buttonBorderColor};
    }
  }

  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    background: ${({ theme }) => theme.buttonBackgroundColor};
    border: 1px solid ${({ theme }) => theme.buttonBorderColor};
    border-radius: 2px !important;

    &:hover {
      border-color: ${({ theme }) => theme.actionText};
      color: ${({ theme }) => theme.actionText};
    }
  }

  .ant-pagination-disabled > .ant-pagination-item-link {
    background: ${({ theme }) => theme.disabledBackground} !important;
    border-color: ${({ theme }) => theme.disabledTextColor} !important;
  }

  /* antd 5 stretches the title beside the full-height filter trigger, so it rides to the top. */
  thead .ant-table-filter-column {
    align-items: center;
  }

  ${({ $draggingTable, theme }) =>
    $draggingTable &&
    `
    box-shadow: 0px 0px 7px 0px ${theme.darkShadow};
    opacity: 0.9;

    thead {
      visibility: collapse;
    }

    .ant-table-row {
      cursor: grabbing !important;
    }
  `}
`;
